/**
 * SendText
 *
 * Support page to help users send text messages via our partner Robocent.
 * Mirrors the Done For You layout and styling.
 */
import React from 'react';
import { Phone, Calendar } from 'lucide-react';
import Footer from '../../components/shared/Footer';
import SEOHead from '../../components/shared/SEOHead';
import { textSupport, sendTextCalendlyUrl, textPricing, gotvSiteUrl } from '../../data/content/textSupportData';
import api from '../../api';

function SendText() {
  return (
    <div className="p-4 sm:p-6 pb-20 w-full max-w-[1600px] mx-auto">
      <SEOHead pageType="app-send-text" />

      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {textSupport.title}
          </h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          {textSupport.subtitle}
        </p>
      </div>

      {/* Live support - section 1 */}
      <div className="mb-6 bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-1">{textSupport.phoneLabel}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Talk to our team for real-time help setting up a compliant texting blast.
        </p>

        <a
          href={`tel:${textSupport.phoneNumberHref}`}
          className="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 px-6 py-3 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          onClick={() => { api.activity?.trackClick('liveSupportClicks'); }}
        >
          <Phone className="h-5 w-5 mr-2" />
          {textSupport.phoneNumberDisplay}
        </a>

        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Available 24/7
        </div>
      </div>

      {/* Prefer to schedule - section 2 */}
      <div className="mb-8 bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-1">{textSupport.scheduleTitle}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          We’ll walk you through best practices and execution options in 15 minutes.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href={sendTextCalendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 px-6 py-3 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            onClick={() => { api.activity?.trackClick('bookCalendlyClicks'); }}
          >
            <Calendar className="h-5 w-5 mr-2" />
            {textSupport.scheduleCta}
          </a>

          <a
            href={textSupport.partnerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 px-6 py-3 text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            onClick={() => { api.activity?.trackClick('visitRobocentClicks'); }}
          >
            <img src="/images/logos/robocent.png" alt="Robocent" className="h-4 w-4 mr-2 object-contain" />
            {textSupport.partnerTitle}
          </a>
        </div>

        <div className="mt-5">
          <a
            href={gotvSiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm px-1.5 py-1 rounded-md text-gray-700 dark:text-gray-300 transition-all duration-200 ease-out hover:text-purple-700 dark:hover:text-purple-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:ring-1 hover:ring-purple-200 dark:hover:ring-purple-800/40 hover:shadow-sm"
          >
            Advocacy Lab is now part of RoboCent / GOTV.com — click here to learn more
          </a>
        </div>
      </div>

      {/* Minimal pricing chart */}
      <div className="mb-8 bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">Pricing Snapshot</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{textPricing.note}</p>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-600 dark:text-gray-400">
                <th className="py-2 pr-4 font-medium">Message Type</th>
                <th className="py-2 pr-4 font-medium">Pay‑as‑You‑Go</th>
                <th className="py-2 pr-4 font-medium">Kickoff</th>
                <th className="py-2 pr-4 font-medium">Grassroots</th>
              </tr>
            </thead>
            <tbody>
              {textPricing.tiers.map((row, i) => (
                <tr key={i} className="border-t border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200">
                  <td className="py-2 pr-4">{row.label}</td>
                  <td className="py-2 pr-4">{row.payg}</td>
                  <td className="py-2 pr-4">{row.kickoff}</td>
                  <td className="py-2 pr-4">{row.grassroots}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{textPricing.footnote}</p>
      </div>

      <Footer />
    </div>
  );
}

export default SendText;


